import React, { useContext, useEffect, useState } from "react";
import { VenueConfigContext } from "../context/VenueConfigContext";
import ConfigModule from "../components/ConfigModule";
import { FaEdit, FaTrash, FaToggleOn, FaToggleOff } from "react-icons/fa";
import PackageMenuManager from "../components/PackageMenuManager";
const API_URL = process.env.REACT_APP_API_URL;


const AdminSettings = () => {
  const { config, loading, fetchConfig } = useContext(VenueConfigContext);

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [taxes, setTaxes] = useState([]);
  const [taxForm, setTaxForm] = useState({
    TaxName: "",
    TaxType: "CGST",
    Percentage: "",
    ApplyOn: "SUBTOTAL",
  });
  const [editingTaxId, setEditingTaxId] = useState(null);

  /* ================= TAX ================= */
  const fetchTaxes = async () => {
    const res = await fetch(`${API_URL}/api/tax`);
    const data = await res.json();
    setTaxes(data);
  };

  /* ================= INIT ================= */
  useEffect(() => {
    if (config) setForm(config);
    fetchTaxes();
  }, [config]);

  /* ================= CONFIG ================= */
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setSuccess(false);
  };

  const handleSave = async () => {
    setSaving(true);
    await fetch(`${API_URL}/api/config`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });

    await fetchConfig();
    setSaving(false);
    setSuccess(true);
  };

  /* ================= LOGO ================= */
  const handleLogoUpload = async () => {
    if (!selectedLogo) return alert("Select file");

    const formData = new FormData();
    formData.append("logo", selectedLogo);

    await fetch(`${API_URL}/api/config/logo`, {
      method: "POST",
      body: formData,
    });

    fetchConfig();
  };

  /* ================= TAX ================= */
  const handleTaxChange = (e) => {
    setTaxForm({ ...taxForm, [e.target.name]: e.target.value });
  };

  const handleTaxSubmit = async () => {
    const method = editingTaxId ? "PUT" : "POST";
    const url = editingTaxId
      ? `${API_URL}/api/tax/${editingTaxId}`
      : `${API_URL}/api/tax`;

    await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(taxForm),
    });

    setTaxForm({
      TaxName: "",
      TaxType: "CGST",
      Percentage: "",
      ApplyOn: "SUBTOTAL",
    });

    setEditingTaxId(null);
    fetchTaxes();
  };

  const handleTaxEdit = (tax) => {
    setTaxForm(tax);
    setEditingTaxId(tax.Id);
  };

  const handleTaxDelete = async (id) => {
    if (!window.confirm("Delete this tax?")) return;
    await fetch(`${API_URL}/api/tax/${id}`, { method: "DELETE" });
    fetchTaxes();
  };

  const handleTaxToggle = async (id) => {
    await fetch(`${API_URL}/api/tax/toggle/${id}`, {
      method: "PATCH",
    });
    fetchTaxes();
  };

  /* ================= LOADING ================= */
  if (loading || !form) {
    return (
      <div className="h-screen flex items-center justify-center text-gray-500">
        Loading configuration...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-5">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* ================= VENUE ================= */}
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <h2 className="text-2xl font-bold mb-4 text-center border-b pb-2">Venue Configuration</h2>

          {/* LOGO */}
          <div className="mb-6">
            <h4 className="text-xs text-gray-500 mb-2 uppercase">
              Logo Management
            </h4>

            {form.Logo && (
              <img
                src={`${API_URL}${form.Logo}`}
                className="w-24 mb-3 rounded"
                alt="logo"
              />
            )}

            <div className="flex items-center gap-3">
              <input
                type="file"
                onChange={(e) => setSelectedLogo(e.target.files[0])}
                className="text-sm"
              />
              <button
                onClick={handleLogoUpload}
                className="bg-teal-600 text-white px-4 py-2 rounded-md text-sm hover:bg-teal-700"
              >
                Upload
              </button>
            </div>
          </div>

          {/* BASIC INFO */}
          <div className="mb-6">
            <h4 className="text-xs text-gray-500 mb-2 uppercase">
              Basic Info
            </h4>

            <div className="grid md:grid-cols-2 gap-3">
              <InputField label="Venue Name" name="Name" value={form.Name} onChange={handleChange} />
              <InputField label="Address" name="Address" value={form.Address} onChange={handleChange} />
            </div>
          </div>

          {/* CONTACT */}
          <div className="mb-6">
            <h4 className="text-xs text-gray-500 mb-2 uppercase">
              Contact
            </h4>

            <div className="grid md:grid-cols-2 gap-3">
              <InputField label="Phone" name="Phone" value={form.Phone} onChange={handleChange} />
              <InputField label="Email" name="Email" value={form.Email} onChange={handleChange} />
            </div>
          </div>

          {/* SAVE */}
          <div className="flex justify-between items-center">
            {success && <span className="text-green-600 text-sm">✔ Saved</span>}

            <button
              onClick={handleSave}
              className="bg-teal-600 text-white px-5 py-2 rounded-md text-sm hover:bg-teal-700"
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>

        {/* ================= TAX ================= */}
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <h2 className="text-2xl font-bold mb-6 text-center border-b pb-2">Tax Configuration</h2>

          <div className="flex flex-wrap gap-3 mb-4">

            <input
              name="TaxName"
              placeholder="Tax Name"
              value={taxForm.TaxName}
              onChange={handleTaxChange}
              className="border rounded-md px-3 py-2 text-sm"
            /> 

            <select
              name="TaxType"
              value={taxForm.TaxType}
              onChange={handleTaxChange}
              className="border rounded-md px-3 py-2 text-sm"
            >
              <option value="CGST">CGST</option>
              <option value="SGST">SGST</option>
              <option value="IGST">IGST</option>
              <option value="SERVICE">Service</option>
            </select>

            <input
              name="Percentage"
              type="number"
              placeholder="Percentage"
              value={taxForm.Percentage}
              onChange={handleTaxChange}
              className="border rounded-md px-3 py-2 text-sm"
            />

            <select
              name="ApplyOn"
              value={taxForm.ApplyOn}
              onChange={handleTaxChange}
              className="border rounded-md px-3 py-2 text-sm"
            >
              <option value="SUBTOTAL">Subtotal</option>
              <option value="FOOD">Food</option>
              <option value="HALL">Hall</option>
            </select>

            <button
              onClick={handleTaxSubmit}
              className="bg-teal-600 text-white px-4 py-2 rounded-md text-sm"
            >
              {editingTaxId ? "Update" : "Add"}
            </button>
          </div>

          {taxes.map((tax) => (
            <div
              key={tax.Id}
              className="flex justify-between items-center border rounded-md px-4 py-2 mb-2"
            >
              <div>
                <strong>{tax.TaxName}</strong> ({tax.TaxType})
                <div className="text-xs text-gray-500">
                  {tax.Percentage}% • {tax.ApplyOn}
                </div>
              </div>

              <div className="flex items-center gap-4 text-sm">
  
  {/* EDIT */}
  <FaEdit
    className="text-blue-500 cursor-pointer hover:scale-110 transition"
    title="Edit"
    onClick={() => handleTaxEdit(tax)}
  />

  {/* TOGGLE */}
  {tax.IsActive ? (
    <FaToggleOn
      className="text-green-500 cursor-pointer text-lg hover:scale-110 transition"
      title="Disable"
      onClick={() => handleTaxToggle(tax.Id)}
    />
  ) : (
    <FaToggleOff
      className="text-gray-400 cursor-pointer text-lg hover:scale-110 transition"
      title="Enable"
      onClick={() => handleTaxToggle(tax.Id)}
    />
  )}

  {/* DELETE */}
  <FaTrash
    className="text-red-500 cursor-pointer hover:scale-110 transition"
    title="Delete"
    onClick={() => handleTaxDelete(tax.Id)}
  />
</div>
            </div>
          ))}
        </div>

        {/* ================= MODULES ================= */}
        <ConfigModule title="Event Types" api={`${API_URL}/api/event-types`} fields={[{ name: "EventName", label: "Event Name" }]} />
        <ConfigModule
  title="Halls"
  api={`${API_URL}/api/halls`}
  fields={[
    { name: "Name", label: "Hall Name" },
    { name: "Price", label: "Price" },
    { name: "Capacity", label: "Capacity" },

    // 👇 NEW FIELD
    {
    name: "childHallIds",
    // label: "Merge With",
    type: "multiselect"
  }
  ]}
/>
        <ConfigModule title="Packages" api={`${API_URL}/api/packages`} fields={[
          { name: "PackageName", label: "Package Name" },
          { name: "Price", label: "Price" },
        ]} />
        <PackageMenuManager />
        <ConfigModule title="Services" api={`${API_URL}/api/services`} fields={[
          { name: "ServiceName", label: "Service Name" },
          { name: "DefaultPrice", label: "Price" },
        ]} />

      </div>
    </div>
  );
};

/* ================= INPUT ================= */
const InputField = ({ label, name, value, onChange }) => (
  <div>
    <label className="text-xs text-gray-500 mb-1 block">{label}</label>
    <input
      name={name}
      value={value || ""}
      onChange={onChange}
      className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 outline-none"
    />
  </div>
);

export default AdminSettings;